import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  ChipPropsColorOverrides,
  darken,
  InputAdornment,
  lighten,
  Paper,
  styled,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import {
  DataGrid,
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridInitialState,
  GridPaginationModel,
  GridSortItem,
  useGridApiRef,
} from '@mui/x-data-grid';
import moment from 'moment';
import { useUrlQuery } from '../../../../hooks/useUrlQuery';
import { avatarUrl, isJsonString, toNumber } from '../../../../utils/functions';
import ProfileTooltip from '../../../../components/ProfileTooltip';
import api from '../../../../services/api';
import opos from '..';
import InputSearchAsync, { ValueProps } from '../../../../components/inputs/InputSearchAsync';
import Filters, { FiltersInputsProps } from '../../../../components/Filters';
import FiltersDrawer from '../../../../components/FiltersDrawer';

type OpOsDataType = {
  empresa: number;
  filial: number;
  origem: string;
  desc_origem: string;
  op: number;
  estagio: number;
  desc_estagio: string;
  seq_roteiro: number;
  operacao: string;
  des_operacao: string;
  qtd_prevista: number;
  qtd_rea1: number;
  qtd_rea2: number;
  qtd_rea3: number;
  qtd_refugo: number;
  qtd_qualidade: number;
  qtd_pendente: number;
  unidade_medida: string;
  desc_unidade_medida: string;
  semana: number;
  ano: number;
  data_inicio_prevista: string;
  data_fim_prevista: string;
  data_inicio: string;
  hora_inicio: string;
  tempo_previsto_dia: number;
  tempo_previsto: string;
  tempo_bruto: string;
  diferenca: number;
  situacao: number;
  des_situacao: number;
  obs_sistema: string;
  pedido: number;
  item: number;
  marca: string;
  desc_marca: string;
  derivacao_marca: string;
  desc_derivacao_marca: string;
  entregavel: string;
  desc_entregavel: string;
  derivacao_entregavel: string;
  desc_derivacao_entregavel: string;
  cliente: number;
  nome_cliente: string;
};

type OpOsResponseType = {
  current_page: number;
  data_limit: number;
  data_total: number;
  total_page: number;
  sorting: GridSortItem[];
  filter: GridFilterItem[];
  data: OpOsDataType[];
};

function RenderStatus(status: '1' | '2' | '3') {
  const label = {
    3: 'EM ATRASO',
    2: 'VENCENDO',
    1: 'NO PRAZO',
  };

  const color: ChipPropsColorOverrides = {
    3: 'error',
    2: 'warning',
    1: 'success',
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Chip size="small" label={label[status]} variant="outlined" color={color[status]} />
    </Box>
  );
}

const getBackgroundColor = (color: string, theme: Theme, coefficient: number) => ({
  backgroundColor: darken(color, coefficient),
  ...theme.applyStyles('light', {
    backgroundColor: lighten(color, coefficient),
  }),
});

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .opos-table-row--2': {
    ...getBackgroundColor(theme.palette.warning.main, theme, 0.8),
    '&:hover': {
      ...getBackgroundColor(theme.palette.warning.main, theme, 0.7),
    },
  },
  '& .opos-table-row--1': {
    ...getBackgroundColor(theme.palette.success.main, theme, 0.8),
    '&:hover': {
      ...getBackgroundColor(theme.palette.success.main, theme, 0.7),
    },
  },
  '& .opos-table-row--3': {
    ...getBackgroundColor(theme.palette.error.main, theme, 0.8),
    '&:hover': {
      ...getBackgroundColor(theme.palette.error.main, theme, 0.7),
    },
  },
}));

function OpOsQueue() {
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const isDarkMode = theme.palette.mode === 'dark';
  const [isFetching, setIsFetching] = useState(false);
  const urlQuery = useUrlQuery();
  const navigate = useNavigate();
  const [initialGridState, setInitialGridState] = useState<GridInitialState>(
    JSON.parse(localStorage?.getItem('opos-queue-grid')) ?? {},
  );

  const [data, setData] = useState<OpOsResponseType>({
    current_page: 1,
    data_limit: 10,
    filter: [],
    sorting: [],
    data_total: 0,
    total_page: 1,
    data: [],
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: toNumber(urlQuery.get('current_page') ?? '0'),
    pageSize: toNumber(urlQuery.get('data_limit') ?? '15'),
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: JSON.parse(isJsonString(urlQuery.get('filter')) ? urlQuery.get('filter') : '[]') ?? [],
  });
  const [showDrawer, setShowDrawer] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortItem[]>(JSON.parse(urlQuery.get('sorting') ?? '[]') ?? []);
  const [inputSituationValue, setInputSituationValue] = useState<{ label: string; id: string }[]>([]);
  const [inputSearchOrigemValue, setInputSearchOrigemValue] = useState<{ label: string; id: string }[]>([]);
  const [inputSortingValue, setInputSortingValue] = useState<{ label: string; field: string; sort: string }[]>([]);

  const situationColor = {
    1: theme.palette.success.main,
    2: theme.palette.warning.main,
    3: theme.palette.error.main,
  };

  const columns: GridColDef[] = [
    {
      field: 'acao',
      headerName: 'Ação',
      disableExport: true,
      filterable: false,
      sortable: false,
      pinnable: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: params => (
        <Button size="small" variant="outlined">
          Iniciar
        </Button>
      ),
    },
    {
      field: 'des_situacao',
      headerName: 'Situação',
      headerAlign: 'center',
      align: 'center',
      renderCell: params => {
        if (!params.value) {
          return '';
        }
        return RenderStatus(params.row.situacao);
      },
    },
    { field: 'origem', headerName: 'ORIGEM', headerAlign: 'center', align: 'center' },
    { field: 'desc_origem', headerName: 'DESC. ORIGEM' },
    { field: 'op', headerName: 'O.P./O.S', headerAlign: 'center', align: 'center' },
    { field: 'seq_roteiro', headerName: 'SEQ.', headerAlign: 'center', align: 'center' },
    { field: 'operacao', headerName: 'OPERAÇÃO' },
    { field: 'des_operacao', headerName: 'DESC. OPERAÇÃO' },
    { field: 'pedido', headerName: 'PEDIDO', headerAlign: 'center', align: 'center' },
    { field: 'cliente', headerName: 'CLIENTE', headerAlign: 'center', align: 'center' },
    { field: 'nome_cliente', headerName: 'NOME CLIENTE' },
    { field: 'marca', headerName: 'ENTREGÁVEL', headerAlign: 'center', align: 'center' },
    { field: 'desc_entregavel', headerName: 'DESC. ENTREGÁVEL' },
    { field: 'semana', headerName: 'SEMANA', type: 'number', headerAlign: 'center', align: 'center' },
    {
      field: 'ano',
      headerName: 'ANO',
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      renderCell: params => params.value.toString().replace(/\./g, ''),
    },
    {
      field: 'data_inicio_prevista',
      headerName: 'INÍCIO PREVISTO',
      type: 'date',
      headerAlign: 'center',
      align: 'center',
      valueGetter: ({ value }) => value && new Date(value),
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              height: '100%',
              color: moment().isAfter(moment(params.row.data_inicio_prevista, 'DD/MM/YYYY'), 'date')
                ? situationColor[3]
                : moment().isSame(moment(params.row.data_inicio_prevista, 'DD/MM/YYYY'), 'date')
                  ? situationColor[2]
                  : situationColor[1],
            }}
          >
            {`${params.row.data_inicio_prevista}`}
          </Box>
        );
      },
    },
    {
      field: 'data_fim_prevista',
      headerName: 'TÉRMINO PREVISTO',
      type: 'date',
      headerAlign: 'center',
      align: 'center',
      valueGetter: ({ value }) => value && new Date(value),
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              height: '100%',
              color: moment().isAfter(moment(params.row.data_fim_prevista, 'DD/MM/YYYY'), 'date')
                ? situationColor[3]
                : moment().isSame(moment(params.row.data_fim_prevista, 'DD/MM/YYYY'), 'date')
                  ? situationColor[2]
                  : situationColor[1],
            }}
          >
            {`${params.row.data_fim_prevista}`}
          </Box>
        );
      },
    },
    { field: 'tempo_previsto', headerName: 'TEMPO PREVISTO', headerAlign: 'center', align: 'center' },
    {
      field: 'tempo_bruto',
      headerName: 'TEMPO GASTO',
      headerAlign: 'center',
      align: 'center',
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: 'center',
              height: '100%',
              color: parseFloat(params.row.diferenca) > 100 ? situationColor[3] : situationColor[1],
            }}
          >
            {`${params.row.tempo_bruto} (${parseFloat(params.row.diferenca).toFixed(1)}%)`}
          </Box>
        );
      },
    },
    {
      field: 'qtd_prevista',
      headerName: 'QTD. PREVISTA',
      headerAlign: 'center',
      align: 'center',
      renderCell: params => {
        return (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'right', height: '100%' }}>
            {`${params.row.qtd_prevista} (${params.row.unidade_medida})`}
          </Box>
        );
      },
    },
    {
      field: 'qtd_pendente',
      headerName: 'QTD. PENDENTE',
      headerAlign: 'center',
      align: 'center',
      renderCell: params => {
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'right', height: '100%' }}>
            {`${params.row.qtd_pendente} (${params.row.unidade_medida})`}
          </Box>
        );
      },
    },
    { field: 'obs_sistema', headerName: 'OBS' },
  ].map(c => {
    return { ...c, width: toNumber(initialGridState?.columns?.dimensions?.[c.field]?.width) } as GridColDef;
  });

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem('opos-queue-grid', JSON.stringify(currentState));
    }
  }, [apiRef]);

  const fetcher = async () => {
    try {
      if (isFetching) return;
      setIsFetching(true);

      const situation = {
        field: 'situacao',
        operator: 'isAnyOf',
        type: 'number',
        value: inputSituationValue.map(v => v.id),
      };
      const origem = {
        field: 'origem',
        operator: 'isAnyOf',
        type: 'string',
        value: inputSearchOrigemValue.map(v => v.id),
      };

      const sorting = [...sortModel, ...inputSortingValue.map(s => ({ field: s.field, sort: s.sort }))];

      const tableFilter = filterModel?.items.map((v: GridFilterItem) => ({
        ...v,
        type: columns.find(c => c.field === v.field)?.type || 'string',
      }));

      const filter = [situation, origem, ...tableFilter];

      const params = {
        current_page: paginationModel.page.toString(),
        data_limit: paginationModel.pageSize.toString(),
        sorting,
        filter,
      };

      const urlSearchParams = new URLSearchParams({
        view: 'queue',
        current_page: params.current_page,
        data_limit: params.data_limit,
        filter: JSON.stringify(params.filter ?? []),
        sorting: JSON.stringify(params.sorting ?? []),
      });

      navigate({ search: urlSearchParams?.toString() });

      const { data: OpOs } = await api.get<OpOsResponseType>('erp/opos/queue', {
        params,
      });

      setData(OpOs);
    } catch (error) {
      console.error(error);
    }
    setIsFetching(false);
  };

  useEffect(() => {
    fetcher();
  }, [paginationModel, sortModel, filterModel, inputSituationValue, inputSortingValue, inputSearchOrigemValue]);

  useLayoutEffect(() => {
    window.addEventListener('beforeunload', saveSnapshot);
    return () => {
      window.removeEventListener('beforeunload', saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  const renderInputSearchOrigem = useMemo(
    () => (
      <InputSearchAsync
        fullWidth
        size="small"
        source="erp-origem"
        id="op-queue-search-origem"
        onChange={(e: ValueProps[]) => {
          setInputSearchOrigemValue(e.map(v => ({ id: v.id, label: v.subdescription })));
        }}
        value={inputSearchOrigemValue.map(v => ({ id: v.id, description: v.id, subdescription: v.label }))}
        label="Origem (+)"
        // startAdornment={<InputAdornment position="start" sx={{ minHeight: 36 }} />}
        sx={{ minWidth: 240, flex: 1 }}
      />
    ),
    [inputSearchOrigemValue],
  );

  const renderDataGrid = useMemo(() => {
    return (
      <StyledDataGrid
        disableColumnSorting
        disableColumnFilter
        showColumnVerticalBorder
        showCellVerticalBorder
        apiRef={apiRef}
        getRowId={(row: OpOsDataType) =>
          `${row.empresa}${row.origem}${row.op}${row.estagio}${row.seq_roteiro}${row.marca}`
        }
        rows={(data?.data as any) || []}
        pageSizeOptions={[10, 15, 30, 50, 100]}
        columns={columns}
        pagination
        sortingMode="server"
        filterMode="server"
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        // onSortModelChange={setSortModel}
        onFilterModelChange={setFilterModel}
        filterModel={filterModel}
        sortModel={sortModel}
        loading={isFetching}
        rowCount={data?.data_total || 0}
        paginationModel={paginationModel}
        disableRowSelectionOnClick
        density="standard"
        getRowClassName={params => `opos-table-row--${params.row.situacao}`}
        initialState={initialGridState}
      />
    );
  }, [data, isFetching, initialGridState]);

  const renderSortingInput = useMemo(() => {
    const list = columns
      .filter(f => f.sortable === undefined || f.sortable)
      .flatMap(c => [
        { label: `${c.headerName} ⬇️`, field: c.field, sort: 'asc' },
        { label: `${c.headerName} ⬆️`, field: c.field, sort: 'desc' },
      ]);

    return (
      <Autocomplete
        multiple
        isOptionEqualToValue={(option: any, value: any) => option.field === value.field}
        value={inputSortingValue}
        onChange={(event: any, newValue: { label: string; field: string; sort: string }[] | null) => {
          setInputSortingValue(newValue);
        }}
        size="small"
        id="input-filter-sorting"
        filterSelectedOptions
        options={list}
        getOptionLabel={option => option.label}
        renderInput={params => <TextField {...params} variant="outlined" size="small" label="Ordenação (+)" />}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <Chip key={key} variant="outlined" label={option.label} size="small" {...tagProps} />;
          })
        }
      />
    );
  }, [inputSortingValue]);

  const renderSituationFilterInput = useMemo(() => {
    const list = [
      { label: 'NO PRAZO', id: '1' },
      { label: 'VENCENDO', id: '2' },
      { label: 'EM ATRASO', id: '3' },
    ];

    return (
      <Autocomplete
        multiple
        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
        value={inputSituationValue}
        onChange={(event: any, newValue: { label: string; id: string }[] | null) => {
          setInputSituationValue(newValue);
        }}
        size="small"
        id="tags-standard"
        filterSelectedOptions
        options={list}
        getOptionLabel={option => option.label}
        renderOption={(props, option, state, ownerState) => {
          const { ...optionProps } = props;
          const color = {
            '3': theme.palette.error.main,
            '2': theme.palette.warning.main,
            '1': theme.palette.success.main,
          };

          return (
            <Box key={option.id} component="li" {...optionProps}>
              <Typography color={color[option.id]}>{ownerState.getOptionLabel(option)}</Typography>
            </Box>
          );
        }}
        renderInput={params => <TextField {...params} variant="outlined" size="small" label="Situação (+)" />}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            const color: ChipPropsColorOverrides = {
              '3': 'error',
              '2': 'warning',
              '1': 'success',
            };
            return (
              <Chip
                key={key}
                variant="outlined"
                label={option.label}
                size="small"
                color={color[option.id]}
                {...tagProps}
              />
            );
          })
        }
      />
    );
  }, [inputSituationValue]);
  const filtersInputs = [
    {
      type: 'combobox',
      id: 'users-limit',
      label: 'Limite',
      defaultValue: 10,
      comboBoxItems: [
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
    },
    {
      type: 'text',
      id: 'users-login',
      label: 'Login',
      defaultValue: '',
    },
    {
      type: 'text',
      id: 'users-name',
      label: 'Nome',
      defaultValue: '',
    },
    {
      type: 'text',
      id: 'users-job',
      label: 'Cargo',
      defaultValue: '',
    },
    {
      type: 'combobox',
      id: 'users-situation',
      label: 'Situação',
      defaultValue: -1,
      comboBoxItems: [
        { value: -1, text: 'Todos' },
        { value: 0, text: 'Inativo' },
        { value: 1, text: 'Ativo' },
      ],
    },
    {
      type: 'date',
      id: 'users-last-access',
      label: 'Ultimo Acesso',
      defaultValue: '',
    },
  ] as FiltersInputsProps[];

  const searchInputs = columns.map(
    c => ({ type: 'text', label: c.headerName, id: c.field, defaultValue: '' }) as FiltersInputsProps,
  );
  const openFilters = () => {
    setShowDrawer(true);
  };
  return (
    <Box display="flex" gap={2} flex={1} flexDirection="column">
      <Button onClick={() => openFilters()}>OK</Button>
      <FiltersDrawer
        drawerId="opos-queue"
        showDrawer={showDrawer}
        onClose={() => setShowDrawer(false)}
        inputs={[
          { id: 'erp-origem', type: 'erp-origem' },
          // { id: 'categoria', type: 'autocomplete' },
          { id: 'nome', type: 'text' },
          { id: 'preco-min', type: 'number' },
          { id: 'data-criacao', type: 'date' },
        ]}
        onApply={() => console.log('Aplicar filtros')}
      />
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {renderSituationFilterInput}
          {renderInputSearchOrigem}
          {renderSortingInput}
        </Box>
      </Paper>
      <Box sx={{ height: 'calc(100vh - 360px)', width: '100%' }}>{renderDataGrid}</Box>
    </Box>
  );
}

export { OpOsQueue };
